import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Mail, UserPlus, Briefcase } from "lucide-react";
import { toast } from "sonner";

// Helper: render en permission-sektion med grid
function PermissionSection({ title, icon, bgClass, filterFn, modulePermLabels, userLevels, modulePermissions, updateModulePermission }) {
  const entries = Object.entries(modulePermLabels).filter(([key]) => filterFn(key));
  if (entries.length === 0) return null;
  return (
    <div className="border-b border-border">
      <div className={`px-4 py-2 ${bgClass} text-sm font-semibold`}>{icon} {title}</div>
      {entries.map(([key, label]) => (
        <div key={key} className="grid grid-cols-6 gap-2 p-3 border-b border-border/50 hover:bg-muted/20">
          <div className="text-sm">{label}</div>
          {userLevels.map(level => (
            <div key={level} className="text-center">
              {level === 'admin' ? (
                <span className="text-green-500 font-bold">✓</span>
              ) : (
                <input type="checkbox" checked={modulePermissions[level]?.[key] ?? false} onChange={(e) => updateModulePermission(level, key, e.target.checked)} className="w-5 h-5 rounded border-2 cursor-pointer accent-primary" data-testid={`perm-${level}-${key}`} />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function AdgangTab({ token, eventId, onCountChange, showConfirm, closeConfirm }) {
  const [modulePermissions, setModulePermissions] = useState({});
  const [modulePermLabels, setModulePermLabels] = useState({});
  const [userLevels, setUserLevels] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  
  // Admin Assistenter
  const [adminAssistants, setAdminAssistants] = useState([]);
  const [showAddAssistant, setShowAddAssistant] = useState(false);
  const [newAssistant, setNewAssistant] = useState({ name: '', email: '', password: '', assigned_event_ids: [] });
  
  // Arrangører
  const [arrangører, setArrangører] = useState([]);
  const [showAddArrangør, setShowAddArrangør] = useState(false);
  const [newArrangør, setNewArrangør] = useState({ name: '', email: '', password: '', assigned_event_ids: [] });
  
  const [allEvents, setAllEvents] = useState([]);
  // Diagnose: fanger fejl så brugeren kan se dem i UI'et (ikke kun i console)
  const [loadDiag, setLoadDiag] = useState({ assistants: null, arrangører: null, events: null, lastLoad: null });
  const [showDiag, setShowDiag] = useState(false);
  // Bruger-opslag (bruges til at finde "usynlige" konti som ikke er admin_assistant/arrangør)
  const [diagSearchEmail, setDiagSearchEmail] = useState('');
  const [diagSearchResults, setDiagSearchResults] = useState(null);
  const [diagSearching, setDiagSearching] = useState(false);

  const searchUserByEmail = async () => {
    const q = (diagSearchEmail || '').trim().toLowerCase();
    if (!q) { toast.error('Skriv en e-mail eller navn'); return; }
    setDiagSearching(true);
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` }});
      const matches = (res.data || []).filter(u =>
        (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q)
      );
      setDiagSearchResults(matches);
      if (matches.length === 0) toast.info('Ingen brugere fundet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Søgning fejlede');
    } finally {
      setDiagSearching(false);
    }
  };

  const deleteAnyUser = async (user) => {
    if (!user?.id) return;
    if (!window.confirm(`Slet '${user.name}' <${user.email}> (rolle: ${user.role})?\n\nDette fjerner brugeren helt fra platformen.`)) return;
    try {
      await axios.delete(`${API}/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Slettet: ${user.name}`);
      setDiagSearchResults(prev => (prev || []).filter(u => u.id !== user.id));
      loadAdminAssistants();
      loadArrangører();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette');
    }
  };

  const loadModulePermissions = useCallback(async () => {
    try {
      setLoadingPermissions(true);
      const res = await axios.get(`${API}/module-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModulePermissions(res.data.permissions || {});
      setModulePermLabels(res.data.labels || {});
      const levels = res.data.user_levels || [];
      setUserLevels(levels);
      if (onCountChange) onCountChange(levels.length);
    } catch (error) {
      console.error("Kunne ikke hente modul-rettigheder", error);
    } finally {
      setLoadingPermissions(false);
    }
  }, [token, onCountChange]);

  const loadAdminAssistants = async () => {
    try {
      const res = await axios.get(`${API}/users/admin-assistants`, { headers: { Authorization: `Bearer ${token}` }});
      setAdminAssistants(res.data);
      setLoadDiag(d => ({ ...d, assistants: { ok: true, count: Array.isArray(res.data) ? res.data.length : 0, status: res.status, sample: Array.isArray(res.data) ? res.data.slice(0, 5).map(u => ({ id: u.id, name: u.name, email: u.email, role: u.role, assigned_event_ids: u.assigned_event_ids })) : res.data }, lastLoad: new Date().toISOString() }));
    } catch (error) {
      console.error("Kunne ikke hente admin assistenter", error);
      setLoadDiag(d => ({ ...d, assistants: { ok: false, status: error?.response?.status, error: error?.response?.data?.detail || error?.message }, lastLoad: new Date().toISOString() }));
    }
  };

  const loadArrangører = async () => {
    try {
      const res = await axios.get(`${API}/users/arrangører`, { headers: { Authorization: `Bearer ${token}` }});
      setArrangører(res.data);
      setLoadDiag(d => ({ ...d, arrangører: { ok: true, count: Array.isArray(res.data) ? res.data.length : 0, status: res.status } }));
    } catch (error) {
      console.error("Kunne ikke hente arrangører", error);
      setLoadDiag(d => ({ ...d, arrangører: { ok: false, status: error?.response?.status, error: error?.response?.data?.detail || error?.message } }));
    }
  };

  const loadAllEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` }});
      const filtered = res.data.filter(e => !e.archived);
      setAllEvents(filtered);
      setLoadDiag(d => ({ ...d, events: { ok: true, count: filtered.length, ids: filtered.map(e => ({ id: e.id, name: e.name })) } }));
    } catch (error) {
      console.error("Kunne ikke hente events", error);
      setLoadDiag(d => ({ ...d, events: { ok: false, status: error?.response?.status, error: error?.response?.data?.detail || error?.message } }));
    }
  };

  useEffect(() => {
    loadModulePermissions();
    loadAdminAssistants();
    loadArrangører();
    loadAllEvents();
  }, [loadModulePermissions]);

  const updateModulePermission = async (level, key, value) => {
    try {
      await axios.put(`${API}/module-permissions/${level}/${key}`, { value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModulePermissions(prev => ({ ...prev, [level]: { ...prev[level], [key]: value }}));
      toast.success("Rettighed opdateret");
    } catch (error) {
      toast.error("Fejl ved opdatering af rettighed");
    }
  };

  // Fælles håndtering af 409 email_exists: tilbud om at tilføje event(s) til eksisterende bruger
  const handleEmailExistsConflict = (error, newEventIds, roleLabel, onSuccess) => {
    const detail = error?.response?.data?.detail;
    if (error?.response?.status !== 409 || !detail || detail.code !== 'email_exists') {
      const msg = typeof detail === 'string' ? detail : (detail?.message || error?.message || "Fejl ved oprettelse");
      toast.error(msg);
      return false;
    }
    const existing = detail.existing_user || {};
    const existingIds = existing.assigned_event_ids || [];
    const toAdd = newEventIds.filter(id => !existingIds.includes(id));
    const addingNames = allEvents.filter(e => toAdd.includes(e.id)).map(e => e.name).join(', ') || '(ingen nye)';
    const currentNames = allEvents.filter(e => existingIds.includes(e.id)).map(e => e.name).join(', ') || 'ingen';
    const roleName = {
      admin: 'Admin', admin_assistant: 'Admin-assistent', arrangør: 'Arrangør',
      heart_leader: 'Leder', chauffør: 'Chauffør', sweeper: 'Sweeper', medarbejder: 'Medarbejder',
      gæst: 'Gæst', volunteer: 'Frivillig'
    }[existing.role] || existing.role;

    showConfirm(
      'E-mail allerede registreret',
      `E-mailen tilhører allerede '${existing.name}' (${roleName}) med adgang til: ${currentNames}.\n\nVil du tilføje adgang til: ${addingNames}?\n\nEllers klik Annullér og brug en anden e-mail.`,
      async () => {
        if (toAdd.length === 0) {
          toast.info("Brugeren har allerede adgang til de valgte festivaler");
          closeConfirm();
          return;
        }
        try {
          const merged = [...existingIds, ...toAdd];
          await axios.put(`${API}/users/${existing.id}/assigned-events`,
            { assigned_event_ids: merged },
            { headers: { Authorization: `Bearer ${token}` }}
          );
          toast.success(`Adgang tilføjet for '${existing.name}'`);
          onSuccess?.();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Kunne ikke opdatere adgang");
        }
        closeConfirm();
      },
      { confirmText: 'Tilføj adgang', confirmVariant: 'default' }
    );
    return true;
  };

  // Admin Assistent CRUD
  const createAdminAssistant = async () => {
    if (!newAssistant.name || !newAssistant.email || !newAssistant.password) { toast.error("Udfyld alle påkrævede felter"); return; }
    try {
      await axios.post(`${API}/users/admin-assistant`, newAssistant, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Admin assistent '${newAssistant.name}' oprettet!`);
      setShowAddAssistant(false);
      setNewAssistant({ name: '', email: '', password: '', assigned_event_ids: [] });
      loadAdminAssistants();
    } catch (error) {
      handleEmailExistsConflict(error, newAssistant.assigned_event_ids, 'Admin-assistent', () => {
        setShowAddAssistant(false);
        setNewAssistant({ name: '', email: '', password: '', assigned_event_ids: [] });
        loadAdminAssistants();
      });
    }
  };

  const updateAssistantEvents = async (userId, eventIds) => {
    try {
      await axios.put(`${API}/users/${userId}/assigned-events`, { assigned_event_ids: eventIds }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Event-tildelinger opdateret");
      loadAdminAssistants();
    } catch (error) { toast.error("Fejl ved opdatering"); }
  };

  const deleteAdminAssistant = async (userId, name) => {
    showConfirm('Slet Admin Assistent', `Er du sikker på at du vil slette '${name}'?`, async () => {
      try {
        await axios.delete(`${API}/users/${userId}`, { headers: { Authorization: `Bearer ${token}` }});
        toast.success("Admin assistent slettet");
        loadAdminAssistants();
      } catch (error) { toast.error(error.response?.data?.detail || "Fejl ved sletning"); }
      closeConfirm();
    });
  };

  // Arrangør CRUD
  const createArrangør = async () => {
    if (!newArrangør.name || !newArrangør.email || !newArrangør.password) { toast.error("Udfyld navn, email og adgangskode"); return; }
    try {
      await axios.post(`${API}/users/arrangør`, newArrangør, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Arrangør '${newArrangør.name}' oprettet!`);
      setShowAddArrangør(false);
      setNewArrangør({ name: '', email: '', password: '', assigned_event_ids: [] });
      loadArrangører();
    } catch (error) {
      handleEmailExistsConflict(error, newArrangør.assigned_event_ids, 'Arrangør', () => {
        setShowAddArrangør(false);
        setNewArrangør({ name: '', email: '', password: '', assigned_event_ids: [] });
        loadArrangører();
      });
    }
  };

  const updateArrangørEvents = async (userId, eventIds) => {
    try {
      await axios.put(`${API}/users/${userId}/assigned-events`, { assigned_event_ids: eventIds }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Event-tildelinger opdateret");
      loadArrangører();
    } catch (error) { toast.error("Fejl ved opdatering"); }
  };

  const deleteArrangør = async (userId, name) => {
    showConfirm('Slet Arrangør', `Er du sikker på at du vil slette '${name}'?`, async () => {
      try {
        await axios.delete(`${API}/users/${userId}`, { headers: { Authorization: `Bearer ${token}` }});
        toast.success("Arrangør slettet");
        loadArrangører();
      } catch (error) { toast.error(error.response?.data?.detail || "Fejl ved sletning"); }
      closeConfirm();
    });
  };

  // Sektion-definitioner for rettigheds-tabellen
  const sections = [
    { title: 'Moduler', icon: '📦', bgClass: 'bg-primary/10', filter: k => k.startsWith('modul_') },
    { title: 'Kort', icon: '🗺️', bgClass: 'bg-blue-500/10', filter: k => k.startsWith('kort_') },
    { title: 'Frivillige', icon: '👥', bgClass: 'bg-purple-500/10', filter: k => k.startsWith('frivillige_') },
    { title: 'Ressourceøer', icon: '♻️', bgClass: 'bg-teal-500/10', filter: k => k.startsWith('ressourceoe_') },
    { title: 'Ressourcespande', icon: '🗑️', bgClass: 'bg-teal-500/10', filter: k => k.startsWith('ressourcespand_') },
    { title: 'Opmærksomhedspunkter', icon: '⚠️', bgClass: 'bg-red-500/10', filter: k => k.startsWith('opmaerkningspunkt_') },
    { title: 'Særlige punkter (vand/pause/snack/kaffe/toilet/pissoir)', icon: '⭐', bgClass: 'bg-cyan-500/10', filter: k => k.startsWith('spec_punkt_') },
    { title: 'Maxicontainere', icon: '📦', bgClass: 'bg-orange-500/10', filter: k => k.startsWith('maxicontainer_') },
    { title: 'Food & Drinks (bod/bar)', icon: '🍔', bgClass: 'bg-amber-500/10', filter: k => k.startsWith('food_drinks_') || k === 'kan_se_food_drinks_noter' },
    { title: 'Zoner', icon: '🗂️', bgClass: 'bg-green-500/10', filter: k => k.startsWith('zoner_') },
    { title: 'GPS-Tracking', icon: '📍', bgClass: 'bg-indigo-500/10', filter: k => k.startsWith('gps_tracker_') },
    { title: 'Funktioner & Teams', icon: '🎭', bgClass: 'bg-amber-500/10', filter: k => k.includes('rolle_') || k.includes('hovedrolle_') },
    { title: 'Event-adgang', icon: '🔐', bgClass: 'bg-red-500/10', filter: k => k === 'alle_events' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Adgangsstyring</h2>
        <p className="text-sm text-muted-foreground">Konfigurer hvilke moduler og handlinger de forskellige bruger-niveauer har adgang til</p>
      </div>

      {loadingPermissions ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-muted-foreground mt-2">Henter rettigheder...</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-6 gap-2 p-4 bg-muted/30 border-b border-border font-medium text-sm">
            <div>Rettighed</div>
            <div className="text-center">Admin</div>
            <div className="text-center">Admin Assistent</div>
            <div className="text-center">Arrangør</div>
            <div className="text-center">Leder</div>
            <div className="text-center">Frivillig</div>
          </div>
          {sections.map(s => (
            <PermissionSection key={s.title} title={s.title} icon={s.icon} bgClass={s.bgClass} filterFn={s.filter} modulePermLabels={modulePermLabels} userLevels={userLevels} modulePermissions={modulePermissions} updateModulePermission={updateModulePermission} />
          ))}
        </div>
      )}
      
      {/* Admin Assistenter */}
      <div className="mt-8 bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 bg-primary/10 flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><UserPlus className="h-5 w-5" />Admin Assistenter</h3>
            <p className="text-sm text-muted-foreground">Brugere med begrænset admin-adgang til specifikke events</p>
          </div>
          <Button onClick={() => setShowAddAssistant(true)} size="sm" data-testid="add-admin-assistant-btn"><Plus className="h-4 w-4 mr-1" />Tilføj</Button>
        </div>

        {/* Diagnose-linje: viser hvad backend faktisk returnerede */}
        {loadDiag.assistants && (
          <div className="px-4 py-2 text-xs border-b border-border/50 flex items-center justify-between bg-muted/20" data-testid="assistants-diag-line">
            {loadDiag.assistants.ok ? (
              <span className="text-muted-foreground">Indlæst {loadDiag.assistants.count} assistent(er) fra server (HTTP {loadDiag.assistants.status})</span>
            ) : (
              <span className="text-red-400">Kunne ikke hente listen — HTTP {loadDiag.assistants.status}: {typeof loadDiag.assistants.error === 'string' ? loadDiag.assistants.error : JSON.stringify(loadDiag.assistants.error)}</span>
            )}
            <button onClick={() => { setShowDiag(true); loadAdminAssistants(); loadArrangører(); loadAllEvents(); }} className="text-primary hover:underline" data-testid="show-diag-btn">Diagnose</button>
          </div>
        )}
        <div className="divide-y divide-border">
          {adminAssistants.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground"><p>Ingen admin assistenter oprettet endnu</p></div>
          ) : adminAssistants.map(assistant => (
            <div key={assistant.id} className="p-4 hover:bg-muted/20">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{assistant.name}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{assistant.email}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteAdminAssistant(assistant.id, assistant.name)} className="text-red-400 hover:text-red-500 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3">
                <Label className="text-xs text-muted-foreground">Tildelte events:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {allEvents.map(event => {
                    const isAssigned = assistant.assigned_event_ids?.includes(event.id);
                    return (
                      <label key={event.id} className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${isAssigned ? 'bg-primary/20 border-primary text-primary' : 'bg-muted/20 border-border hover:border-primary/50'}`}>
                        <input type="checkbox" checked={isAssigned} onChange={(e) => { const newEventIds = e.target.checked ? [...(assistant.assigned_event_ids || []), event.id] : (assistant.assigned_event_ids || []).filter(id => id !== event.id); updateAssistantEvents(assistant.id, newEventIds); }} className="w-3 h-3 rounded" />
                        {event.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Opret Admin Assistent Dialog */}
      {showAddAssistant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddAssistant(false)}>
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" />Opret Admin Assistent</h3>
            <div className="space-y-4">
              <div><Label>Navn *</Label><Input value={newAssistant.name} onChange={(e) => setNewAssistant({...newAssistant, name: e.target.value})} placeholder="F.eks. Festival Koordinator" /></div>
              <div><Label>Email *</Label><Input type="email" value={newAssistant.email} onChange={(e) => setNewAssistant({...newAssistant, email: e.target.value})} placeholder="assistent@festival.dk" /></div>
              <div><Label>Adgangskode *</Label><Input type="password" value={newAssistant.password} onChange={(e) => setNewAssistant({...newAssistant, password: e.target.value})} placeholder="Vælg en sikker adgangskode" /></div>
              <div>
                <Label className="text-sm">Tildel til events (valgfrit)</Label>
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-2 bg-muted/20 rounded">
                  {allEvents.map(event => (
                    <label key={event.id} className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${newAssistant.assigned_event_ids.includes(event.id) ? 'bg-primary/20 border-primary text-primary' : 'bg-background border-border hover:border-primary/50'}`}>
                      <input type="checkbox" checked={newAssistant.assigned_event_ids.includes(event.id)} onChange={(e) => { const newIds = e.target.checked ? [...newAssistant.assigned_event_ids, event.id] : newAssistant.assigned_event_ids.filter(id => id !== event.id); setNewAssistant({...newAssistant, assigned_event_ids: newIds}); }} className="w-3 h-3 rounded" />
                      {event.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={createAdminAssistant} className="flex-1"><Plus className="h-4 w-4 mr-1" />Opret</Button>
              <Button variant="outline" onClick={() => setShowAddAssistant(false)}>Annuller</Button>
            </div>
          </div>
        </div>
      )}

      {/* Arrangører */}
      <div className="mt-8 bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 bg-amber-500/10 flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Briefcase className="h-5 w-5" />Arrangører</h3>
            <p className="text-sm text-muted-foreground">Event-arrangører med begrænset adgang til stamdata, kort og ressourcer</p>
          </div>
          <Button onClick={() => setShowAddArrangør(true)} size="sm" data-testid="add-arrangør-btn"><Plus className="h-4 w-4 mr-1" />Tilføj</Button>
        </div>
        <div className="divide-y divide-border">
          {arrangører.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground"><p>Ingen arrangører oprettet endnu</p></div>
          ) : arrangører.map(arr => (
            <div key={arr.id} className="p-4 hover:bg-muted/20">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{arr.name}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{arr.email}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteArrangør(arr.id, arr.name)} className="text-red-400 hover:text-red-500 hover:bg-red-500/10" data-testid={`delete-arrangør-${arr.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3">
                <Label className="text-xs text-muted-foreground">Tildelte events:</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {allEvents.map(event => {
                    const isAssigned = arr.assigned_event_ids?.includes(event.id);
                    return (
                      <label key={event.id} className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${isAssigned ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'bg-muted/20 border-border hover:border-amber-500/50'}`}>
                        <input type="checkbox" checked={isAssigned} onChange={(e) => { const newEventIds = e.target.checked ? [...(arr.assigned_event_ids || []), event.id] : (arr.assigned_event_ids || []).filter(id => id !== event.id); updateArrangørEvents(arr.id, newEventIds); }} className="w-3 h-3 rounded" />
                        {event.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Opret Arrangør Dialog */}
      {showAddArrangør && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddArrangør(false)}>
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Briefcase className="h-5 w-5 text-amber-500" />Opret Arrangør</h3>
            <div className="space-y-4">
              <div><Label>Navn *</Label><Input value={newArrangør.name} onChange={(e) => setNewArrangør({...newArrangør, name: e.target.value})} placeholder="F.eks. Event Koordinator" data-testid="arrangør-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={newArrangør.email} onChange={(e) => setNewArrangør({...newArrangør, email: e.target.value})} placeholder="arrangør@event.dk" data-testid="arrangør-email-input" /></div>
              <div><Label>Adgangskode *</Label><Input type="password" value={newArrangør.password} onChange={(e) => setNewArrangør({...newArrangør, password: e.target.value})} placeholder="Vælg en sikker adgangskode" data-testid="arrangør-password-input" /></div>
              <div>
                <Label className="text-sm">Tildel til events (valgfrit)</Label>
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-2 bg-muted/20 rounded">
                  {allEvents.map(event => (
                    <label key={event.id} className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${newArrangør.assigned_event_ids.includes(event.id) ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'bg-background border-border hover:border-amber-500/50'}`}>
                      <input type="checkbox" checked={newArrangør.assigned_event_ids.includes(event.id)} onChange={(e) => { const newIds = e.target.checked ? [...newArrangør.assigned_event_ids, event.id] : newArrangør.assigned_event_ids.filter(id => id !== event.id); setNewArrangør({...newArrangør, assigned_event_ids: newIds}); }} className="w-3 h-3 rounded" />
                      {event.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={createArrangør} className="flex-1" data-testid="submit-arrangør-btn"><Plus className="h-4 w-4 mr-1" />Opret</Button>
              <Button variant="outline" onClick={() => setShowAddArrangør(false)}>Annuller</Button>
            </div>
          </div>
        </div>
      )}

      {/* Diagnose Dialog - viser rådata fra backend så bruger kan sende det til support */}
      {showDiag && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowDiag(false)}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="diag-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Diagnose — rådata fra server</h3>
              <button onClick={() => setShowDiag(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Send dette til support hvis noget ikke vises korrekt. Sidste opdatering: {loadDiag.lastLoad || 'ukendt'}</p>
            <pre className="text-xs bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap break-all" data-testid="diag-json">{JSON.stringify({
              admin_assistants: loadDiag.assistants,
              arrangører: loadDiag.arrangører,
              events: loadDiag.events,
              current_state: {
                assistants_in_state: adminAssistants.length,
                arrangører_in_state: arrangører.length,
                events_in_state: allEvents.length,
                assistants_sample: adminAssistants.slice(0, 5).map(u => ({ id: u.id, name: u.name, email: u.email, role: u.role, assigned_event_ids: u.assigned_event_ids }))
              }
            }, null, 2)}</pre>

            {/* Find og slet "usynlige" brugere - fx tidligere konti med anden rolle */}
            <div className="mt-4 border-t border-border pt-4">
              <h4 className="text-sm font-semibold mb-2">Find bruger på e-mail eller navn</h4>
              <p className="text-xs text-muted-foreground mb-2">
                Hvis en bruger blev oprettet med anden rolle (fx frivillig) vises de ikke i listerne ovenfor. Søg her og slet hvis nødvendigt.
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="F.eks. sm1203@hotmail.com eller Steen"
                  value={diagSearchEmail}
                  onChange={(e) => setDiagSearchEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') searchUserByEmail(); }}
                  data-testid="diag-search-input"
                />
                <Button onClick={searchUserByEmail} disabled={diagSearching} data-testid="diag-search-btn">
                  {diagSearching ? 'Søger...' : 'Find'}
                </Button>
              </div>
              {diagSearchResults !== null && (
                <div className="mt-3 space-y-2" data-testid="diag-search-results">
                  {diagSearchResults.length === 0 && (
                    <p className="text-xs text-muted-foreground">Ingen match fundet.</p>
                  )}
                  {diagSearchResults.map(u => {
                    const eventNames = (u.assigned_event_ids || [])
                      .map(id => allEvents.find(e => e.id === id)?.name || id)
                      .join(', ') || 'ingen';
                    return (
                      <div key={u.id} className="border border-border rounded p-3 flex items-start justify-between gap-2 bg-background">
                        <div className="text-xs flex-1">
                          <div className="font-medium text-sm">{u.name}</div>
                          <div className="text-muted-foreground">{u.email}</div>
                          <div className="mt-1"><span className="font-semibold">Rolle:</span> <span className="text-primary">{u.role || '(ingen)'}</span></div>
                          <div><span className="font-semibold">Tildelte events:</span> {eventNames}</div>
                          <div className="text-muted-foreground mt-1 opacity-60">id: {u.id}</div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteAnyUser(u)}
                          data-testid={`diag-delete-${u.id}`}
                        >
                          Slet
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => { navigator.clipboard?.writeText(document.querySelector('[data-testid="diag-json"]')?.textContent || ''); toast.success('Kopieret'); }} data-testid="copy-diag-btn">Kopiér til udklipsholder</Button>
              <Button variant="outline" onClick={() => { loadAdminAssistants(); loadArrangører(); loadAllEvents(); toast.info('Genindlæser...'); }}>Genindlæs</Button>
              <Button variant="ghost" onClick={() => setShowDiag(false)}>Luk</Button>
            </div>
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground p-4 bg-muted/20 rounded-lg">
        <p><strong>Tips:</strong></p>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li><strong>Admin</strong> har altid fuld adgang og kan ikke ændres</li>
          <li><strong>Admin Assistent</strong> kan kun se events de er tildelt til</li>
          <li><strong>Arrangør</strong> er event-arrangører med adgang til stamdata, kort og ressourcer - men IKKE frivillige eller adgangsstyring</li>
          <li><strong>Leder</strong> er frivillige markeret som ledere (is_leader = true)</li>
          <li><strong>Frivillig</strong> er alle almindelige frivillige</li>
        </ul>
      </div>
    </div>
  );
}
